import {
  EventNoteRounded,
  GroupRounded,
  Groups,
  HailRounded,
  Person,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  type Mission,
  type TaskTypes,
  type TaskPriority,
} from "@shared/entities/mission/entity";
import HourglassFullRoundedIcon from '@mui/icons-material/HourglassFullRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import { format } from "date-fns";
import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MissionDetails } from "../../../components/mission-details/MissionDetails";
import MissionDialog from "./MissionDialog";
import dayjs from "dayjs";

const priorityColors: Record<
  TaskPriority,
  "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
> = {
  MEDIUM: "info",
  HIGH: "warning",
  CRITICAL: "error",
  LOW: "default",
} as const;

interface MissionCardProps {
  mission: Mission;
  onUpdate?: () => void;
  isSelected?: boolean;
  onSelect?: (missionId: string, selected: boolean) => void;
  isSelectionMode?: boolean;
}

const LONG_PRESS_DURATION = 500; // 500ms for long press

export const MissionCard: React.FC<MissionCardProps> = ({
  mission,
  onUpdate,
  isSelected = false,
  onSelect,
  isSelectionMode = false,
}) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const pressTimer = useRef<NodeJS.Timeout>();
  const [isPressing, setIsPressing] = useState(false);
  const longPressTriggered = useRef(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const navigate = useNavigate();

  const getRelatedEntityName = (type: TaskTypes) => {
    if (type == "USER")
      return (
        <Tooltip key={type} title={`מתנדב: ${mission.association?.userName}`}>
          <IconButton
            size="small"
            sx={{
              backgroundColor: "#e3f2fd",
              color: "navy",
            }}
            onClick={(event) => {
              event.preventDefault();
              navigate(`/volunteers/${mission.association?.id}`, {
                state: { table: "volunteers" },
              });
            }}
          >
            <Person fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    if (type == "GROUP" && mission.group?.name)
      return (
        <Tooltip key={type} title={`קבוצה מאורגנת: ${mission.group?.name}`}>
          <IconButton
            size="small"
            sx={{
              backgroundColor: "#f3e5f5",
              color: "navy",
            }}
            onClick={(event) => {
              event.preventDefault();
              navigate(`/volunteers-groups/${mission.group?.id}`);
            }}
          >
            <Groups fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    if (type == "OPPORTUNITY")
      return (
        <Tooltip key={type} title={`משימה: ${mission.opportunity?.title}`}>
          <IconButton
            size="small"
            sx={{
              backgroundColor: "#fff3e0",
              color: "navy",
            }}
            onClick={(event) => {
              event.preventDefault();
              navigate(`/events/${mission.opportunity?.id}`);
            }}
          >
            <EventNoteRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    if (type == "AID_RELATED")
      return (
        <Tooltip key={type} title={`מבקש סיוע: ${mission.association?.user?.name}`}>
          <IconButton
            size="small"
            sx={{
              backgroundColor: "#e8f5e9",
              color: "navy",
            }}
            onClick={(event) => {
              event.preventDefault();
              navigate(`/aid-related`);
            }}
          >
            <HailRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    if (type == "TEAM")
      return (
        <Tooltip key={type} title={`פרויקט: ${mission.team?.name}`}>
          <IconButton
            size="small"
            sx={{
              backgroundColor: "#f3fbb6",
              color: "navy",
            }}
            onClick={(event) => {
              event.preventDefault();
              navigate(`/volunteers/${mission.team?.id}`, {
                state: { table: "teams" },
              });
            }}
          >
            <GroupRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      );
  };

  const handlePressStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      longPressTriggered.current = false;
      setIsPressing(true);

      pressTimer.current = setTimeout(() => {
        longPressTriggered.current = true;
        onSelect?.(mission.id, true);
        setIsPressing(false);
      }, LONG_PRESS_DURATION);
    },
    [mission.id, onSelect],
  );

  const handlePressEnd = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      setIsPressing(false);

      if (pressTimer.current) {
        clearTimeout(pressTimer.current);
      }

      // Only trigger click if it wasn't a long press
      if (!longPressTriggered.current) {
        if (isSelectionMode) {
          onSelect?.(mission.id, !isSelected);
        } else {
          // Only open details if we're not in selection mode
          setIsDetailsOpen(true);
        }
      }
    },
    [isSelectionMode, mission.id, isSelected, onSelect],
  );

  const isOverdue = new Date(mission.date) < new Date();
  const is2HoursOverdue = dayjs(mission.date).isAfter(dayjs().subtract(2, "hours"));

  return (
    <>
      <Card
        sx={{
          mb: 1,
          borderLeft: 4,
          borderColor:
            mission.status === "PENDING"
              ? "#ef4444"
              : mission.status === "DONE"
                ? "#22c55e"
                : "#fbbf24",
          backgroundColor: isSelected ? "action.selected" : "background.paper",
          opacity: isPressing ? 0.7 : 1,
          height: "unset",
          minHeight: "unset",
          justifyContent: "flex-start",
          transition: "all 0.2s ease",
          borderRadius: 3,
          cursor: isSelectionMode ? "pointer" : "default",
          "&:active": {
            transform: isPressing ? "scale(0.98)" : "none",
          },
        }}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={() => {
          if (pressTimer.current) {
            clearTimeout(pressTimer.current);
          }
          setIsPressing(false);
        }}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchCancel={(e) => {
          if (pressTimer.current) {
            clearTimeout(pressTimer.current);
          }
          setIsPressing(false);
        }}
      >
        <CardContent
          sx={{
            width: "100%",
            padding: "0.6rem 1rem",
            [":last-child"]: {
              paddingBottom: "0.4rem",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack flexDirection="row" alignItems="flex-start" justifyContent="center" gap={0.5}>
              <Typography
                variant="subtitle2"
                fontSize="small"
                color="text.secondary"
              >
                {format(new Date(mission.date), "dd/MM/yyyy HH:mm")}
              </Typography>
              {isOverdue && mission.status !== "DONE" && (
                <Tooltip title="עבר זמן המטלה">
                  <HourglassFullRoundedIcon
                    color="error"
                    fontSize="small"
                  />
                </Tooltip>
              )}
              {!isOverdue && is2HoursOverdue && mission.status !== "DONE" && (
                <Tooltip title="עוד שעתיים נשארו לביצוע המטלה">
                  <HourglassTopRoundedIcon
                    color="warning"
                    fontSize="small"
                  />
                </Tooltip>
              )}
            </Stack>
            <IconButton
              size="small"
              disabled={isSelectionMode}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsEditDialogOpen(true);
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              sx={{ marginRight: -1 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>

          <Tooltip
            placement="bottom-end"
            enterDelay={600}
            enterNextDelay={600}
            title={mission.description}
            disableHoverListener={mission.description.length < 100}
          >
            <Typography
              variant="body1"
              fontSize="small"
              textOverflow="ellipsis"
              overflow="hidden"
              gutterBottom
            >
              {mission.description}
            </Typography>
          </Tooltip>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              mt: 1,
              alignItems: "center",
            }}
          >
            <Chip
              label={mission.priority}
              size="small"
              color={priorityColors[mission.priority as TaskPriority]}
              sx={{ fontWeight: 520, fontSize: "x-small" }}
            />
            {mission.types?.map((type) => getRelatedEntityName(type))}
            {mission.creatorManager?.name && (
              <Typography
                variant="caption"
                fontSize="small"
                display="block"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                יוצר: {mission.creatorManager.name}
              </Typography>
            )}
            {mission.assignedManager?.name && (
              <Typography
                variant="caption"
                fontSize="small"
                display="block"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                אחראי: {mission.assignedManager.name}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <MissionDetails
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        mission={mission}
      />

      <MissionDialog
        isOpen={isEditDialogOpen}
        setIsOpen={setIsEditDialogOpen}
        mission={mission}
        onSuccess={() => onUpdate?.()}
      />
    </>
  );
};
